import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Button,
  useMediaQuery,
  Popover,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DataGrid, gridClasses, GridToolbarContainer } from "@mui/x-data-grid";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import { NAVBAR_HEIGHT } from "constants/layout";
import { OVERLAY_NAMES } from "constants/Overlays";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import ViewListIcon from "@mui/icons-material/ViewListRounded";
import ViewModuleIcon from "@mui/icons-material/ViewModuleRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Chip,
} from "@mui/material";
import dayjs from "dayjs";
import api from "services/api";
import ExpensesFilterSelector from "components/ExpensesFilterSelector";
import { FILTER_SELECTOR_WIDTH } from "constants/filterSelectors";
import DataGridHeader from "components/DataGridHeader";
import ColumnFilter from "components/ColumnFilter";
import ColumnDateFilter from "components/ColumnDateFilter";
import useColumnFilters from "hooks/useColumnFilters";
import { numericComparator } from "helpers/ServiceHelpers";
import CEQuickFilter from "components/CEQuickFilter";
import {
  formatExpenseDate,
  getExpenseDateDayjs,
} from "./expenseDateUtils";

const Expenses = () => {
  usePageTitle("Expenses");
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const {
    handleErrorDialog,
    openDialog,
    closeDialog,
    showSnackbar,
    setOverlayData,
    setOverlayName,
  } = useStateContext();

  const [isLoading, setIsLoading] = useState(true);
  const [listOfExpenses, setListOfExpenses] = useState([]);
  const [minMaxDates, setMinMaxDates] = useState();

  // Year selector data
  const currentYear = dayjs().year();

  const getSavedYear = () => {
    const savedYear = localStorage.getItem("EXPENSES_YEAR_KEY");
    return savedYear ? JSON.parse(savedYear) : [String(currentYear)];
  };

  const getSavedQuickFilter = () => {
    const saved = localStorage.getItem("EXPENSES_QUICK_FILTER");
    try {
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  };

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Get saved months from localStorage or use current month as default
  const getSavedMonths = () => {
    const savedMonths = localStorage.getItem("EXPENSES_MONTHS_KEY");
    return savedMonths ? JSON.parse(savedMonths) : [dayjs().format("MMM")];
  };

  const getSavedStatuses = () => {
    const savedStatuses = localStorage.getItem("EXPENSES_STATUS_KEY");

    if (!savedStatuses) {
      return ["Finalized", "Draft"];
    }

    try {
      const parsed = JSON.parse(savedStatuses);
      if (!Array.isArray(parsed)) {
        return ["Finalized", "Draft"];
      }

      const validStatuses = parsed.filter(
        (status) => status === "Finalized" || status === "Draft",
      );

      return validStatuses.length > 0 ? validStatuses : ["Finalized", "Draft"];
    } catch (e) {
      return ["Finalized", "Draft"];
    }
  };

  const [selectedMonths, setSelectedMonths] = useState(getSavedMonths);

  const [yearOptions, setYearOptions] = useState([String(currentYear)]);
  const [selectedYear, setSelectedYear] = useState(getSavedYear);
  const [selectedStatuses, setSelectedStatuses] = useState(getSavedStatuses);

  const [contextMenu, setContextMenu] = useState(null);
  const [selectedRow, setSelectedRow] = useState();
  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const longPressTimerRef = useRef(null);
  const suppressNextClickRef = useRef(false);
  const LONG_PRESS_MS = 500;

  // Global filter state management
  const [openFilter, setOpenFilter] = useState(null);
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: getSavedQuickFilter(),
  });
  const [sortModel, setSortModel] = useState([]);

  // View mode state (persisted)
  const getSavedViewMode = () => {
    const saved = localStorage.getItem("EXPENSES_VIEW_MODE");
    return saved === "grid" ? "grid" : "list";
  };
  const [viewMode, setViewMode] = useState(getSavedViewMode); // 'list' or 'grid'

  // Generate dynamic year options when minMaxDates is fetched
  useEffect(() => {
    if (minMaxDates && minMaxDates.startDate && minMaxDates.endDate) {
      const minYear = minMaxDates.startDate.year;
      const maxYear = minMaxDates.endDate.year;

      const years = [];
      for (let year = minYear; year <= maxYear; year++) {
        years.push(String(year));
      }

      // Always include current year if not in range
      if (!years.includes(String(currentYear))) {
        years.push(String(currentYear));
        years.sort();
      }

      setYearOptions(years);
    }
  }, [minMaxDates, currentYear]);

  // Handle expenses filter save
  const handleExpensesFilterSave = (newSelection) => {
    const {
      months: newMonths,
      years: newYears,
      statuses: newStatuses,
    } = newSelection;

    const nextStatuses =
      newStatuses && newStatuses.length > 0
        ? newStatuses
        : ["Finalized", "Draft"];

    // Update state
    setSelectedMonths(newMonths);
    setSelectedYear(newYears);
    setSelectedStatuses(nextStatuses);

    // Save to localStorage
    localStorage.setItem("EXPENSES_MONTHS_KEY", JSON.stringify(newMonths));
    localStorage.setItem("EXPENSES_YEAR_KEY", JSON.stringify(newYears));
    localStorage.setItem("EXPENSES_STATUS_KEY", JSON.stringify(nextStatuses));

    // Fetch data
    fetchExpensesData(newMonths, newYears);
  };

  // Fetch expenses data
  const fetchExpensesData = useCallback(
    (months = selectedMonths, year = selectedYear) => {
      setIsLoading(true);

      // Ensure we have valid parameters
      let finalMonths = months;
      let finalYear = year;

      if (!finalMonths || finalMonths.length === 0) {
        finalMonths = [dayjs().format("MMM")];
      }

      if (!finalYear || finalYear.length === 0) {
        finalYear = [String(currentYear)];
      }

      api
        .get("/expenses", {
          params: {
            year: finalYear[0] || currentYear,
            months: JSON.stringify(finalMonths),
          },
        })
        .then((response) => {
          setListOfExpenses(response.data);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching expenses:", error);
          handleErrorDialog(`Error fetching expenses: ${error.message}`, theme);
          setIsLoading(false);
        });
    },
    [selectedMonths, selectedYear, currentYear, handleErrorDialog, theme],
  );

  // Fetch min/max dates
  useEffect(() => {
    api
      .get("/dashboard/serviceDateRange")
      .then((response) => {
        setMinMaxDates(response.data);
      })
      .catch((error) => {
        console.error("Error fetching expense date range:", error);
      });
  }, []);

  // Fetch expenses when months or year changes
  useEffect(() => {
    fetchExpensesData();
  }, [fetchExpensesData]);

  const dateComparator = (v1, v2) => {
    return dayjs(v1).unix() - dayjs(v2).unix();
  };

  const columns = [
    {
      field: "ExpenseDate",
      headerName: "Date",
      valueGetter: (_, params) => {
        return formatExpenseDate(params.ExpenseDate);
      },
      sortComparator: dateComparator,
      minWidth: 120,
      flex: 1.2,
      renderHeader: (params) => {
        const columnIndex = filterColumns.findIndex(
          (col) => col.field === params.field,
        );
        return (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnDateFilter
              data={columnIndex >= 0 ? filterData[columnIndex] : []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChange(params.field, values)
              }
              onSortChange={(field, direction) => {
                setSortModel([{ field, sort: direction }]);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              sortModel={sortModel}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        );
      },
    },
    {
      field: "Status",
      headerName: "Status",
      valueGetter: (_, params) => {
        return params.Status || "Draft";
      },
      minWidth: 130,
      flex: 1.1,
      renderCell: (params) => {
        const status = params.value || "Draft";
        const isFinalized = status === "Finalized";

        return (
          <Chip
            label={status}
            size="small"
            sx={{
              fontWeight: 700,
              minWidth: 90,
              color: isFinalized
                ? theme.palette.success.dark
                : theme.palette.warning.dark,
              backgroundColor: isFinalized
                ? theme.palette.success.light
                : theme.palette.warning.light,
            }}
          />
        );
      },
      renderHeader: (params) => {
        const columnIndex = filterColumns.findIndex(
          (col) => col.field === params.field,
        );
        return (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={columnIndex >= 0 ? filterData[columnIndex] : []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChange(params.field, values)
              }
              onSortChange={(field, direction) => {
                setSortModel([{ field, sort: direction }]);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        );
      },
    },
    {
      field: "Category",
      headerName: "Category",
      valueGetter: (_, params) => {
        return params.Category ? params.Category.Name : "N/A";
      },
      minWidth: 180,
      flex: 1.8,
      renderHeader: (params) => {
        const columnIndex = filterColumns.findIndex(
          (col) => col.field === params.field,
        );
        return (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={columnIndex >= 0 ? filterData[columnIndex] : []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChange(params.field, values)
              }
              onSortChange={(field, direction) => {
                setSortModel([{ field, sort: direction }]);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        );
      },
    },
    {
      field: "SubCategory",
      headerName: "Subcategory",
      valueGetter: (_, params) => {
        return params.SubCategory ? params.SubCategory.Name : "N/A";
      },
      minWidth: 180,
      flex: 1.8,
      renderHeader: (params) => {
        const columnIndex = filterColumns.findIndex(
          (col) => col.field === params.field,
        );
        return (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={columnIndex >= 0 ? filterData[columnIndex] : []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChange(params.field, values)
              }
              onSortChange={(field, direction) => {
                setSortModel([{ field, sort: direction }]);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        );
      },
    },
    {
      field: "Amount",
      headerName: "Amount",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.Amount;
        let value = "N/A";
        if (data !== undefined && data !== null) {
          value =
            parseFloat(data) === 0
              ? "N/A"
              : `$${parseFloat(data).toLocaleString()}`;
        }
        return value;
      },
      getApplyQuickFilterFn: (value) => {
        if (!value) {
          return null;
        }
        return (params) => {
          // params is the cell value directly (formatted string from valueGetter)
          if (params == null || params === "N/A") {
            return false;
          }
          // Strip formatting for search: remove $, commas
          const normalized = String(params).replace(/[$,]/g, "");
          return normalized.toLowerCase().includes(value.toLowerCase());
        };
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: (params) => {
        const columnIndex = filterColumns.findIndex(
          (col) => col.field === params.field,
        );
        return (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={columnIndex >= 0 ? filterData[columnIndex] : []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChange(params.field, values)
              }
              onSortChange={(field, direction) => {
                setSortModel([{ field, sort: direction }]);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        );
      },
    },
    {
      field: "Description",
      headerName: "Description",
      valueGetter: (_, params) => {
        return params.Description || "N/A";
      },
      minWidth: 300,
      flex: 3.0,
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      renderHeader: (params) => {
        const columnIndex = filterColumns.findIndex(
          (col) => col.field === params.field,
        );
        return (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={columnIndex >= 0 ? filterData[columnIndex] : []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChange(params.field, values)
              }
              onSortChange={(field, direction) => {
                setSortModel([{ field, sort: direction }]);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        );
      },
    },
    {
      field: "PaymentMethod",
      headerName: "Payment Method",
      valueGetter: (_, params) => {
        return params.PaymentMethod || "N/A";
      },
      minWidth: 160,
      flex: 1.6,
      renderHeader: (params) => {
        const columnIndex = filterColumns.findIndex(
          (col) => col.field === params.field,
        );
        return (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={columnIndex >= 0 ? filterData[columnIndex] : []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChange(params.field, values)
              }
              onSortChange={(field, direction) => {
                setSortModel([{ field, sort: direction }]);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        );
      },
    },
    {
      field: "ReferenceNumber",
      headerName: "Reference #",
      valueGetter: (_, params) => {
        return params.ReferenceNumber || "N/A";
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: (params) => {
        const columnIndex = filterColumns.findIndex(
          (col) => col.field === params.field,
        );
        return (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={columnIndex >= 0 ? filterData[columnIndex] : []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChange(params.field, values)
              }
              onSortChange={(field, direction) => {
                setSortModel([{ field, sort: direction }]);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        );
      },
    },
  ];

  const statusFilteredExpenses = useMemo(() => {
    if (!Array.isArray(selectedStatuses) || selectedStatuses.length === 0) {
      return [];
    }

    return (listOfExpenses || []).filter((expense) => {
      const status = expense?.Status || "Draft";
      return selectedStatuses.includes(status);
    });
  }, [listOfExpenses, selectedStatuses]);

  // Column filters
  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
  } = useColumnFilters(statusFilteredExpenses || [], columns);

  // Filter columns configuration for cross-filtering
  const filterColumns = useMemo(
    () => [
      {
        field: "ExpenseDate",
        valueGetter: (row) =>
          formatExpenseDate(row.ExpenseDate),
      },
      {
        field: "Status",
        valueGetter: (row) => row.Status || "Draft",
      },
      {
        field: "Category",
        valueGetter: (row) => (row.Category ? row.Category.Name : "N/A"),
      },
      {
        field: "SubCategory",
        valueGetter: (row) => (row.SubCategory ? row.SubCategory.Name : "N/A"),
      },
      {
        field: "Amount",
        valueGetter: (row) => {
          const data = row.Amount;
          let value = "N/A";
          if (data !== undefined && data !== null) {
            value =
              parseFloat(data) === 0
                ? "N/A"
                : `$${parseFloat(data).toLocaleString()}`;
          }
          return value;
        },
      },
      { field: "Description", valueGetter: (row) => row.Description || "N/A" },
      {
        field: "PaymentMethod",
        valueGetter: (row) => row.PaymentMethod || "N/A",
      },
      {
        field: "ReferenceNumber",
        valueGetter: (row) => row.ReferenceNumber || "N/A",
      },
    ],
    [],
  );

  // Cross-filtering data calculation
  const filterData = useMemo(() => {
    return filterColumns.map((column, index) => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([field]) => field !== column.field,
      );

      let filteredData;
      if (otherFilters.length === 0) {
        filteredData = statusFilteredExpenses || [];
      } else {
        filteredData = (statusFilteredExpenses || []).filter((row) => {
          return otherFilters.every(([field, selectedValues]) => {
            if (!selectedValues || selectedValues.length === 0) return true;

            const filterColumn = filterColumns.find(
              (col) => col.field === field,
            );
            if (!filterColumn) return true;

            const value = filterColumn.valueGetter(row);
            return selectedValues.includes(value);
          });
        });
      }

      return filteredData;
    });
  }, [columnFilters, statusFilteredExpenses, filterColumns]);

  // Check if quick filter has values (DataGrid stores quick filter text in quickFilterValues)
  const hasQuickFilter =
    (filterModel?.quickFilterValues?.length || 0) > 0 ||
    (filterModel?.items || []).some((item) => item.operator === "contains");

  // Combined check for any active filters
  const hasAnyActiveFilters = hasActiveFilters || hasQuickFilter;

  const handleFinalizeSelectedExpenses = useCallback(() => {
    if (!selectionModel.length) return;

    const selectedIds = selectionModel.map((id) => Number(id)).filter(Boolean);
    if (!selectedIds.length) return;

    openDialog({
      theme,
      title: "Finalize Selected Expenses",
      message: `Finalize ${selectedIds.length} selected expense${selectedIds.length !== 1 ? "s" : ""}?`,
      primaryAction: "Finalize",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        try {
          setIsLoading(true);

          const results = await Promise.allSettled(
            selectedIds.map((id) => api.post(`/expenses/${id}/finalize`)),
          );

          const successCount = results.filter(
            (r) => r.status === "fulfilled",
          ).length;
          const failedCount = results.length - successCount;

          await fetchExpensesData();
          setSelectionModel([]);
          setSelectionEnabled(false);
          closeDialog();

          if (failedCount === 0) {
            showSnackbar(
              `${successCount} expense${successCount !== 1 ? "s" : ""} finalized`,
              theme,
              "success",
            );
          } else {
            showSnackbar(
              `${successCount} finalized, ${failedCount} failed`,
              theme,
              "warning",
            );
          }
        } catch (error) {
          console.error("Error finalizing selected expenses:", error);
          const errorMessage =
            error?.response?.data?.error ||
            error?.message ||
            "Failed to finalize selected expenses";
          handleErrorDialog(
            `Error finalizing selected expenses: ${errorMessage}`,
            theme,
          );
        } finally {
          setIsLoading(false);
        }
      },
      secondaryOnClick: () => {
        closeDialog();
      },
    });
  }, [
    selectionModel,
    openDialog,
    theme,
    fetchExpensesData,
    closeDialog,
    showSnackbar,
    handleErrorDialog,
  ]);

  // Wrapped clear all filters that also clears quick filter
  const handleClearAllFiltersWithQuickFilter = useCallback(() => {
    clearAllFilters();
    const cleared = { items: [], quickFilterValues: [] };
    setFilterModel(cleared);
    localStorage.setItem(
      "EXPENSES_QUICK_FILTER",
      JSON.stringify(cleared.quickFilterValues),
    );
  }, [clearAllFilters]);

  // Keep toolbar identity stable so quick filter input retains focus
  const clearAllFiltersRef = useRef(handleClearAllFiltersWithQuickFilter);
  useEffect(() => {
    clearAllFiltersRef.current = handleClearAllFiltersWithQuickFilter;
  }, [handleClearAllFiltersWithQuickFilter]);

  const handleToolbarClear = useCallback(() => {
    clearAllFiltersRef.current();
  }, []);

  // Create CustomToolbar
  const CustomToolbar = useMemo(() => {
    const Toolbar = ({
      hasAnyActiveFilters: toolbarHasFilters,
      onClear,
      theme: toolbarTheme,
    }) => {
      const [anchorEl, setAnchorEl] = useState(null);
      const open = Boolean(anchorEl);

      const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
      };

      const handleClose = () => {
        setAnchorEl(null);
      };

      return (
        <GridToolbarContainer
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 0,
          }}
        >
          {!isMobile && selectionEnabled && (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setSelectionModel([]);
                  setSelectionEnabled(false);
                }}
                sx={{
                  mx: 1,
                  color: toolbarTheme.palette.primary.main,
                  borderColor: toolbarTheme.palette.primary.main,
                  "&:hover": {
                    borderColor:
                      toolbarTheme.palette.primary.buttonHoverSelected,
                    backgroundColor:
                      toolbarTheme.palette.primary.buttonHoverSelected,
                    color: toolbarTheme.palette.background.default,
                  },
                }}
              >
                Cancel Selection
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<CheckCircleRoundedIcon fontSize="small" />}
                onClick={handleFinalizeSelectedExpenses}
                disabled={selectionModel.length === 0}
                sx={{
                  mr: 1,
                  color: toolbarTheme.palette.primary.main,
                  borderColor: toolbarTheme.palette.primary.main,
                  "&:hover": {
                    borderColor:
                      toolbarTheme.palette.primary.buttonHoverSelected,
                    backgroundColor:
                      toolbarTheme.palette.primary.buttonHoverSelected,
                    color: toolbarTheme.palette.background.default,
                  },
                }}
              >
                Finalize Selected Expenses
              </Button>
            </>
          )}

          {isMobile && selectionEnabled && (
            <>
              <Tooltip title="Actions">
                <IconButton
                  onClick={handleClick}
                  size="small"
                  sx={{
                    mx: 1,
                    color: toolbarTheme.palette.primary.main,
                    borderColor: toolbarTheme.palette.primary.main,
                    border: `1px solid ${toolbarTheme.palette.primary.main}`,
                    borderRadius: "8px",
                    "&:hover": {
                      borderColor:
                        toolbarTheme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        toolbarTheme.palette.primary.buttonHoverSelected,
                      color: toolbarTheme.palette.background.default,
                    },
                  }}
                >
                  <MoreHorizRoundedIcon />
                </IconButton>
              </Tooltip>

              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Box sx={{ minWidth: 220 }}>
                  <MenuItem
                    onClick={() => {
                      setSelectionModel([]);
                      setSelectionEnabled(false);
                      handleClose();
                    }}
                  >
                    <ListItemText>Cancel Selection</ListItemText>
                  </MenuItem>

                  <MenuItem
                    onClick={() => {
                      handleFinalizeSelectedExpenses();
                      handleClose();
                    }}
                    disabled={selectionModel.length === 0}
                  >
                    <ListItemIcon
                      sx={{ color: toolbarTheme.palette.primary.main }}
                    >
                      <CheckCircleRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Finalize Selected Expenses</ListItemText>
                  </MenuItem>
                </Box>
              </Popover>
            </>
          )}

          {!isMobile && (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
              }}
            >
              {toolbarHasFilters && (
                <Button
                  startIcon={<ClearIcon />}
                  onClick={onClear}
                  variant="outlined"
                  size="small"
                  sx={{
                    color: toolbarTheme.palette.primary.main,
                    borderColor: toolbarTheme.palette.primary.main,
                    "&:hover": {
                      borderColor:
                        toolbarTheme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        toolbarTheme.palette.primary.buttonHoverSelected,
                      color: toolbarTheme.palette.background.default,
                    },
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          )}

          {isMobile && toolbarHasFilters && (
            <Tooltip title="Clear Filters">
              <IconButton
                onClick={onClear}
                size="small"
                sx={{
                  color: toolbarTheme.palette.primary.main,
                  borderColor: toolbarTheme.palette.primary.main,
                  border: `1px solid ${toolbarTheme.palette.primary.main}`,
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor:
                      toolbarTheme.palette.primary.buttonHoverSelected,
                    backgroundColor:
                      toolbarTheme.palette.primary.buttonHoverSelected,
                    color: toolbarTheme.palette.background.default,
                  },
                }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}

          <Box sx={{ flex: 1 }} />

          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <CEQuickFilter iconOnly={isMobile} />
          </Box>
        </GridToolbarContainer>
      );
    };

    return Toolbar;
  }, [isMobile, selectionEnabled, selectionModel, handleFinalizeSelectedExpenses]);

  const handleContextMenu = (event) => {
    event.preventDefault();
    setSelectedRow(Number(event.currentTarget.getAttribute("data-id")));
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    );
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleRowMouseDown = (event) => {
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;

    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressNextClickRef.current = true;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowMouseUp = () => {
    cancelLongPress();
  };

  const handleRowTouchStart = (event) => {
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;

    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressNextClickRef.current = true;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowTouchEnd = () => {
    cancelLongPress();
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const getSelectedExpense = () => {
    if (!selectedRow) return null;
    return (
      (columnFilteredData || []).find((exp) => exp.id === selectedRow) || null
    );
  };

  const handleContextMenuEdit = () => {
    const expense = getSelectedExpense();
    if (!expense) {
      handleContextMenuClose();
      return;
    }

    setOverlayData({
      onSaved: fetchExpensesData,
      selectedYear: selectedYear?.[0] || String(currentYear),
      expense,
    });
    setOverlayName(OVERLAY_NAMES.MANAGEMENT_EXPENSE_NEW_EDIT);
    handleContextMenuClose();
  };

  const handleContextMenuDelete = () => {
    const expense = getSelectedExpense();
    if (!expense) {
      handleContextMenuClose();
      return;
    }

    openDialog({
      theme: theme,
      title: "Delete Expense",
      message: `Are you sure you want to delete this expense dated ${formatExpenseDate(expense.ExpenseDate)} for $${parseFloat(expense.Amount).toLocaleString()}?`,
      primaryAction: "Delete",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        try {
          setIsLoading(true);
          await api.delete(`/expenses/${expense.id}`);
          await fetchExpensesData();
          closeDialog();
          showSnackbar("Expense deleted successfully", theme, "success");
        } catch (error) {
          console.error("Error deleting expense:", error);
          handleErrorDialog(`Error deleting expense: ${error.message}`, theme);
        } finally {
          setIsLoading(false);
          handleContextMenuClose();
        }
      },
      secondaryOnClick: () => {
        closeDialog();
        handleContextMenuClose();
      },
    });
  };

  const handleContextMenuFinalizeMonth = () => {
    const expense = getSelectedExpense();
    if (!expense) {
      handleContextMenuClose();
      return;
    }

    const expenseDate = getExpenseDateDayjs(expense.ExpenseDate);
    if (!expenseDate.isValid()) {
      showSnackbar("Invalid expense date", theme, "error");
      handleContextMenuClose();
      return;
    }

    const targetYear = expenseDate.year();
    const targetMonth = expenseDate.month() + 1;
    const monthLabel = expenseDate.format("MMMM YYYY");

    openDialog({
      theme: theme,
      title: "Finalize Month",
      message: `Finalize all Draft expenses for ${monthLabel}?`,
      primaryAction: "Finalize",
      secondaryAction: "Cancel",
      primaryOnClick: async () => {
        try {
          setIsLoading(true);
          const response = await api.post("/expenses/finalize-month", {
            year: targetYear,
            month: targetMonth,
          });

          await fetchExpensesData();
          closeDialog();

          const finalizedCount = response?.data?.newlyFinalizedCount ?? 0;
          showSnackbar(
            `${monthLabel} finalized (${finalizedCount} updated)`,
            theme,
            "success",
          );
        } catch (error) {
          console.error("Error finalizing expense month:", error);
          const errorMessage =
            error?.response?.data?.error ||
            error?.message ||
            "Failed to finalize month";
          handleErrorDialog(`Error finalizing month: ${errorMessage}`, theme);
        } finally {
          setIsLoading(false);
          handleContextMenuClose();
        }
      },
      secondaryOnClick: () => {
        closeDialog();
        handleContextMenuClose();
      },
    });
  };

  // Calculate totals
  let totalExpenses = 0;
  const dataForTotals = columnFilteredData || [];
  dataForTotals.forEach((expense) => {
    totalExpenses += parseFloat(expense.Amount) || 0;
  });

  const customFooter = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ flexWrap: "wrap", justifyContent: "center" }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Total Expenses:
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: theme?.palette?.secondary?.[400],
              fontWeight: 600,
            }}
          >
            ${Number(totalExpenses).toLocaleString()}
          </Typography>
        </Stack>
      </Box>
    );
  };

  // Grid View Component
  const ExpenseGridView = () => {
    const [previewPosition, setPreviewPosition] = useState(null);
    const [previewCellData, setPreviewCellData] = useState(null);

    // Organize expenses by category/subcategory and month
    const gridData = useMemo(() => {
      const data = {};
      const categoriesSet = new Set();
      const subcategoriesMap = {}; // category -> subcategories
      const monthsSet = new Set();

      columnFilteredData.forEach((expense) => {
        const monthYear = dayjs(expense.ExpenseDate).format("MMM YYYY");
        const category = expense.Category?.Name || "Uncategorized";
        const subcategory = expense.SubCategory?.Name || "General";

        categoriesSet.add(category);
        monthsSet.add(monthYear);
        if (!subcategoriesMap[category]) {
          subcategoriesMap[category] = new Set();
        }
        subcategoriesMap[category].add(subcategory);

        const key = `${category}|${subcategory}|${monthYear}`;
        if (!data[key]) {
          data[key] = {
            total: 0,
            expenses: [],
            monthYear,
            category,
            subcategory,
          };
        }
        data[key].total += parseFloat(expense.Amount) || 0;
        data[key].expenses.push(expense);
      });

      return { data, categoriesSet, subcategoriesMap, monthsSet };
    }, []);

    const {
      data: gridDataMap,
      categoriesSet,
      subcategoriesMap,
      monthsSet,
    } = gridData;

    // Get unique months sorted
    const months = useMemo(() => {
      return Array.from(monthsSet).sort((a, b) => {
        return dayjs(a, "MMM YYYY").valueOf() - dayjs(b, "MMM YYYY").valueOf();
      });
    }, [monthsSet]);

    // Get unique categories sorted with their subcategories
    const categoriesWithSubcategories = useMemo(() => {
      const result = [];
      Array.from(categoriesSet)
        .sort()
        .forEach((category) => {
          const subcategories = Array.from(
            subcategoriesMap[category] || [],
          ).sort();
          result.push({ category, subcategories });
        });
      return result;
    }, [categoriesSet, subcategoriesMap]);

    // Totals per month
    const monthTotals = useMemo(() => {
      const totals = {};
      (columnFilteredData || []).forEach((expense) => {
        const monthYear = dayjs(expense.ExpenseDate).format("MMM YYYY");
        totals[monthYear] =
          (totals[monthYear] || 0) + (parseFloat(expense.Amount) || 0);
      });
      return totals;
    }, []);

    const formatAmount = (value) => {
      return Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    const handlePreviewOpen = (event, cellData) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const panelWidth = 340;
      const prefersLeft = rect.right + panelWidth + 24 > window.innerWidth;

      setPreviewPosition({
        top: Math.max(12, rect.top - 8),
        left: prefersLeft
          ? Math.max(12, rect.left - panelWidth - 12)
          : Math.min(window.innerWidth - panelWidth - 12, rect.right + 12),
      });
      setPreviewCellData(cellData);
    };

    const handlePreviewClose = () => {
      setPreviewPosition(null);
      setPreviewCellData(null);
    };

    const handleCellClick = (monthYear, category, subcategory) => {
      const key = `${category}|${subcategory}|${monthYear}`;
      const cellData = gridDataMap[key];
      if (cellData && cellData.expenses.length > 0) {
        setOverlayData({
          expenses: cellData.expenses,
          category,
          subcategory,
          monthYear,
          selectedYear: selectedYear?.[0] || String(currentYear),
          onSaved: fetchExpensesData,
        });
        setOverlayName(OVERLAY_NAMES.EXPENSE_PROFILE);
      }
    };

    return (
      <Box sx={{ p: 2, overflowX: "auto", height: "100%" }}>
        <TableContainer
          component={Paper}
          sx={{
            bgcolor: theme.palette.background.default,
            maxHeight: "100%",
            "& .MuiTableCell-root": {
              borderColor: theme.palette.divider,
            },
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    bgcolor: theme.palette.background.alt,
                    color: theme.palette.primary.main,
                    minWidth: 70,
                    width: 70,
                    maxWidth: 70,
                    position: "sticky",
                    left: 0,
                    top: 0,
                    zIndex: 3,
                  }}
                >
                  Category
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    bgcolor: theme.palette.background.alt,
                    color: theme.palette.primary.main,
                    minWidth: 100,
                    width: 100,
                    maxWidth: 100,
                    position: "sticky",
                    left: 70,
                    top: 0,
                    zIndex: 3,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  Subcategory
                </TableCell>
                {months.map((monthYear) => (
                  <TableCell
                    key={monthYear}
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      bgcolor: theme.palette.background.alt,
                      color: theme.palette.primary.main,
                      minWidth: 90,
                      width: 90,
                      maxWidth: 90,
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      borderLeft: `1px solid ${theme.palette.divider}`,
                      fontSize: "0.875rem",
                    }}
                  >
                    {monthYear}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Total Row */}
              <TableRow sx={{ bgcolor: theme.palette.background.alt }}>
                <TableCell
                  colSpan={2}
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    position: "sticky",
                    left: 0,
                    zIndex: 3,
                    bgcolor: theme.palette.background.alt,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                  }}
                >
                  Total
                </TableCell>
                {months.map((monthYear) => {
                  const total = monthTotals[monthYear] || 0;
                  return (
                    <TableCell
                      key={`total-${monthYear}`}
                      align="center"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.secondary[400],
                        borderLeft: `1px solid ${theme.palette.divider}`,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        bgcolor: theme.palette.background.alt,
                      }}
                    >
                      $
                      {total.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </TableCell>
                  );
                })}
              </TableRow>
              {categoriesWithSubcategories.map((categoryObj, categoryIdx) => {
                const { category, subcategories } = categoryObj;
                return subcategories.map((subcategory, subcategoryIdx) => {
                  const categoryKey = `${category}|${subcategory}`;
                  const isFirstSubcategory = subcategoryIdx === 0;

                  return (
                    <TableRow key={categoryKey} hover>
                      {isFirstSubcategory && (
                        <TableCell
                          rowSpan={subcategories.length}
                          sx={{
                            fontWeight: 700,
                            position: "sticky",
                            left: 0,
                            zIndex: 2,
                            bgcolor: theme.palette.background.default,
                            verticalAlign: "middle",
                            padding: "8px 4px",
                            minWidth: 70,
                            width: 70,
                            maxWidth: 70,
                          }}
                        >
                          {category}
                        </TableCell>
                      )}
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          position: "sticky",
                          left: 70,
                          zIndex: 2,
                          bgcolor: theme.palette.background.default,
                          borderLeft: `1px solid ${theme.palette.divider}`,
                          minWidth: 100,
                          width: 100,
                          maxWidth: 100,
                        }}
                      >
                        {subcategory}
                      </TableCell>
                      {months.map((monthYear) => {
                        const key = `${category}|${subcategory}|${monthYear}`;
                        const cellData = gridDataMap[key];
                        const hasData =
                          !!cellData && cellData.expenses.length > 0;
                        const draftCount = hasData
                          ? cellData.expenses.filter(
                              (expense) =>
                                (expense.Status || "Draft") === "Draft",
                            ).length
                          : 0;
                        const finalizedCount = hasData
                          ? cellData.expenses.filter(
                              (expense) =>
                                (expense.Status || "Draft") === "Finalized",
                            ).length
                          : 0;

                        return (
                          <TableCell
                            key={`${categoryKey}-${monthYear}`}
                            align="center"
                            sx={{
                              position: "relative",
                              overflow: "visible",
                              cursor: hasData ? "pointer" : "default",
                              fontWeight: hasData ? 600 : 400,
                              color: hasData
                                ? theme.palette.primary.text
                                : theme.palette.primary.text3,
                              borderLeft: `1px solid ${theme.palette.divider}`,
                              zIndex: 1,
                              "&:hover": hasData
                                ? {
                                    bgcolor: theme.palette.action.hover,
                                  }
                                : {},
                            }}
                            onClick={() =>
                              hasData &&
                              handleCellClick(monthYear, category, subcategory)
                            }
                          >
                            {hasData ? (
                              <Tooltip
                                title={`${cellData.expenses.length} expense(s)`}
                              >
                                <Box
                                  onMouseEnter={(event) =>
                                    handlePreviewOpen(event, {
                                      ...cellData,
                                      category,
                                      subcategory,
                                    })
                                  }
                                  onMouseLeave={handlePreviewClose}
                                  sx={{
                                    position: "absolute",
                                    inset: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    px: 0.5,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      position: "relative",
                                      minHeight: 34,
                                      width: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {draftCount > 0 && (
                                      <Box
                                        sx={{
                                          position: "absolute",
                                          top: 3,
                                          left: 4,
                                          minWidth: 18,
                                          height: 18,
                                          px: 0.5,
                                          borderRadius: "6px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          fontSize: "0.7rem",
                                          fontWeight: 700,
                                          lineHeight: 1,
                                          color: theme.palette.warning.dark,
                                          backgroundColor:
                                            theme.palette.warning.light,
                                        }}
                                      >
                                        {draftCount}
                                      </Box>
                                    )}

                                    {finalizedCount > 0 && (
                                      <Box
                                        sx={{
                                          position: "absolute",
                                          top: 3,
                                          right: 4,
                                          minWidth: 18,
                                          height: 18,
                                          px: 0.5,
                                          borderRadius: "6px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          fontSize: "0.7rem",
                                          fontWeight: 700,
                                          lineHeight: 1,
                                          color: theme.palette.success.dark,
                                          backgroundColor:
                                            theme.palette.success.light,
                                        }}
                                      >
                                        {finalizedCount}
                                      </Box>
                                    )}

                                    <span>${formatAmount(cellData.total)}</span>
                                  </Box>
                                </Box>
                              </Tooltip>
                            ) : (
                              <span
                                style={{ color: theme.palette.primary.text3 }}
                              >
                                —
                              </span>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                });
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {previewCellData && previewPosition && (
          <Box
            sx={{
              position: "fixed",
              top: previewPosition.top,
              left: previewPosition.left,
              zIndex: 1500,
              p: 1,
              width: 430,
              borderRadius: "10px",
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.default,
              boxShadow: theme.shadows[8],
              pointerEvents: "none",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {previewCellData.monthYear} • {previewCellData.category} /{" "}
                {previewCellData.subcategory}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: theme.palette.secondary[400] }}
              >
                Total: ${formatAmount(previewCellData.total)}
              </Typography>

              <Box
                sx={{
                  borderTop: `1px solid ${theme.palette.divider}`,
                  pt: 0.75,
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  overflow: "visible",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    borderRadius: "8px",
                    p: 0.75,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      fontWeight: 700,
                      color: theme.palette.warning.dark,
                      backgroundColor: theme.palette.warning.light,
                      borderRadius: "6px",
                      px: 0.75,
                      py: 0.25,
                      mb: 0.5,
                    }}
                  >
                    Draft
                  </Typography>

                  {previewCellData.expenses
                    .filter((expense) => (expense.Status || "Draft") === "Draft")
                    .map((expense) => (
                      <Box key={expense.id} sx={{ mb: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            fontWeight: 600,
                            lineHeight: 1.25,
                          }}
                        >
                          {expense.Description || "No description"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: theme.palette.secondary[400],
                            lineHeight: 1.25,
                          }}
                        >
                          ${formatAmount(expense.Amount)}
                        </Typography>
                      </Box>
                    ))}

                  {previewCellData.expenses.filter(
                    (expense) => (expense.Status || "Draft") === "Draft",
                  ).length === 0 && (
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", display: "block", textAlign: "center" }}
                    >
                      No draft expenses
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{
                    width: "1px",
                    bgcolor: theme.palette.divider,
                    alignSelf: "stretch",
                    borderRadius: "1px",
                  }}
                />

                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    borderRadius: "8px",
                    p: 0.75,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      fontWeight: 700,
                      color: theme.palette.success.dark,
                      backgroundColor: theme.palette.success.light,
                      borderRadius: "6px",
                      px: 0.75,
                      py: 0.25,
                      mb: 0.5,
                    }}
                  >
                    Finalized
                  </Typography>

                  {previewCellData.expenses
                    .filter(
                      (expense) => (expense.Status || "Draft") === "Finalized",
                    )
                    .map((expense) => (
                      <Box key={expense.id} sx={{ mb: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            fontWeight: 600,
                            lineHeight: 1.25,
                          }}
                        >
                          {expense.Description || "No description"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: theme.palette.secondary[400],
                            lineHeight: 1.25,
                          }}
                        >
                          ${formatAmount(expense.Amount)}
                        </Typography>
                      </Box>
                    ))}

                  {previewCellData.expenses.filter(
                    (expense) => (expense.Status || "Draft") === "Finalized",
                  ).length === 0 && (
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", display: "block", textAlign: "center" }}
                    >
                      No finalized expenses
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      p="1rem 1rem 0 1rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Grid container alignItems="center">
          <Grid
            size={{ xs: 12 }}
            container
            justifyContent="flex-end"
            sx={{ px: 1 }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              gap={2}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: isMobile ? "auto" : FILTER_SELECTOR_WIDTH,
                }}
              >
                <ExpensesFilterSelector
                  months={months}
                  years={
                    yearOptions.length > 0 ? yearOptions : [String(currentYear)]
                  }
                  selectedMonths={selectedMonths}
                  selectedYears={selectedYear}
                  selectedStatuses={selectedStatuses}
                  onSave={handleExpensesFilterSave}
                />
              </Box>

              <Box display="flex" gap={isMobile ? 1 : 2} alignItems="center">
                {isMobile ? (
                  <Tooltip
                    title={viewMode === "list" ? "Grid View" : "List View"}
                    placement="top"
                  >
                    <IconButton
                      onClick={() => {
                        const nextMode = viewMode === "list" ? "grid" : "list";
                        setViewMode(nextMode);
                        localStorage.setItem("EXPENSES_VIEW_MODE", nextMode);
                      }}
                      sx={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: theme.palette.background.default,
                        borderRadius: "12px",
                        boxShadow:
                          "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                        color: theme.palette.primary.main,
                        "&:hover": {
                          backgroundColor: theme.palette.primary.buttonHover,
                          boxShadow:
                            "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                        },
                      }}
                    >
                      {viewMode === "list" ? (
                        <ViewModuleIcon />
                      ) : (
                        <ViewListIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Button
                    onClick={() => {
                      const nextMode = viewMode === "list" ? "grid" : "list";
                      setViewMode(nextMode);
                      localStorage.setItem("EXPENSES_VIEW_MODE", nextMode);
                    }}
                    startIcon={
                      viewMode === "list" ? (
                        <ViewModuleIcon />
                      ) : (
                        <ViewListIcon />
                      )
                    }
                    sx={{
                      width: "200px",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      padding: "8px 16px",
                      backgroundColor: theme.palette.background.default,
                      borderRadius: "12px",
                      boxShadow:
                        "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                      color: theme.palette.primary.main,
                      textTransform: "none",
                      fontSize: "0.875rem",
                      "& .MuiButton-startIcon": {
                        marginRight: 0,
                      },
                      "& .MuiButton-endIcon": {
                        display: "none",
                      },
                      "&:hover": {
                        backgroundColor: theme.palette.primary.buttonHover,
                        color: theme.palette.primary.main,
                        boxShadow:
                          "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                      },
                    }}
                  >
                    <Box sx={{ flex: 1, textAlign: "center" }}>
                      {viewMode === "list" ? "Grid View" : "List View"}
                    </Box>
                  </Button>
                )}

                {isMobile ? (
                  <Tooltip title="Add Expense" placement="top">
                    <IconButton
                      onClick={() => {
                        setOverlayData({
                          onSaved: fetchExpensesData,
                          selectedYear:
                            selectedYear?.[0] || String(currentYear),
                        });
                        setOverlayName(
                          OVERLAY_NAMES.MANAGEMENT_EXPENSE_NEW_EDIT,
                        );
                      }}
                      sx={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: "12px",
                        boxShadow:
                          "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                        color: theme.palette.background.default,
                        "&:hover": {
                          backgroundColor:
                            theme.palette.primary.buttonHoverSelected,
                          boxShadow:
                            "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                        },
                      }}
                    >
                      <AddRoundedIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Button
                    onClick={() => {
                      setOverlayData({
                        onSaved: fetchExpensesData,
                        selectedYear: selectedYear?.[0] || String(currentYear),
                      });
                      setOverlayName(OVERLAY_NAMES.MANAGEMENT_EXPENSE_NEW_EDIT);
                    }}
                    startIcon={<AddRoundedIcon />}
                    sx={{
                      width: "200px",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      padding: "8px 16px",
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: "12px",
                      boxShadow:
                        "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                      color: theme.palette.background.default,
                      textTransform: "none",
                      fontSize: "0.875rem",
                      "& .MuiButton-startIcon": {
                        marginRight: 0,
                      },
                      "& .MuiButton-endIcon": {
                        display: "none",
                      },
                      "&:hover": {
                        backgroundColor:
                          theme.palette.primary.buttonHoverSelected,
                        color: theme.palette.background.default,
                        boxShadow:
                          "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                      },
                    }}
                  >
                    <Box sx={{ flex: 1, textAlign: "center" }}>Add Expense</Box>
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box
        borderRadius="12px"
        bgcolor={theme.palette.background.default}
        sx={{
          boxShadow: theme.shadows[3],
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          mb: 1,
          mx: 1,
          mt: 2,
          [`.${gridClasses.cell}`]: {
            color: theme.palette.primary.text,
          },
          [`.${gridClasses.cell}.notAvailable`]: {
            color: theme.palette.primary.text3,
          },
        }}
      >
        {viewMode === "grid" ? (
          <ExpenseGridView />
        ) : (
          <DataGrid
            localeText={{
              noRowsLabel: "No expenses added yet for this period.",
              noResultsOverlayLabel: "No matches for your filters",
            }}
            loading={isLoading}
            pageSizeOptions={[20, 50, 100]}
            getRowId={(row) => row.id}
            rows={columnFilteredData || []}
            columns={columns}
            checkboxSelection={selectionEnabled}
            rowSelectionModel={selectionEnabled ? selectionModel : []}
            onRowSelectionModelChange={(newSelection) => {
              if (!selectionEnabled) return;
              setSelectionModel(newSelection);
            }}
            filterModel={filterModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            disableColumnMenu
            disableColumnSorting
            disableRowSelectionOnClick={!!openFilter || selectionEnabled}
            onRowClick={(params, event) => {
              if (openFilter) {
                setOpenFilter(null);
                return;
              }

              if (selectionEnabled) {
                if (suppressNextClickRef.current) {
                  suppressNextClickRef.current = false;
                  return;
                }

                setSelectionModel((prev) =>
                  prev.includes(params.row.id)
                    ? prev.filter((id) => id !== params.row.id)
                    : [...prev, params.row.id],
                );
              }
            }}
            onRowDoubleClick={(params) => {
              if (selectionEnabled) return;

              const expense = (columnFilteredData || []).find(
                (exp) => exp.id === params.id,
              );
              if (!expense) return;
              setOverlayData({
                onSaved: fetchExpensesData,
                selectedYear: selectedYear?.[0] || String(currentYear),
                expense,
              });
              setOverlayName(OVERLAY_NAMES.MANAGEMENT_EXPENSE_NEW_EDIT);
            }}
            slots={{ footer: customFooter, toolbar: CustomToolbar }}
            onFilterModelChange={(newFilterModel) => {
              setFilterModel(newFilterModel);
              localStorage.setItem(
                "EXPENSES_QUICK_FILTER",
                JSON.stringify(newFilterModel?.quickFilterValues || []),
              );
            }}
            slotProps={{
              row: {
                onContextMenu: handleContextMenu,
                style: { cursor: "context-menu" },
                onMouseDown: handleRowMouseDown,
                onMouseUp: handleRowMouseUp,
                onMouseLeave: handleRowMouseUp,
                onTouchStart: handleRowTouchStart,
                onTouchEnd: handleRowTouchEnd,
                onTouchCancel: handleRowTouchEnd,
              },
              loadingOverlay: {
                variant: "skeleton",
                noRowsVariant: "skeleton",
              },
              toolbar: {
                showQuickFilter: true,
                hasAnyActiveFilters,
                onClear: handleToolbarClear,
                theme,
                printOptions: { disableToolbarButton: true },
                csvOptions: { disableToolbarButton: true },
              },
            }}
            ignoreDiacritics
            sx={{
              backgroundColor: theme.palette.background.default,
              borderRadius: "12px",
              height: "100%",
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "bold",
                color: theme.palette.primary.main,
              },
              "& .MuiDataGrid-scrollbarFiller": {
                backgroundColor: theme.palette.background.alt,
              },
              "& .MuiDataGrid-sortIcon": {
                display: "none !important",
              },
              "& .MuiDataGrid-menuIconButton": {
                display: "none !important",
              },
              "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
                display: "none !important",
              },
              "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
                display: "none !important",
              },
              "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                flex: 1,
              },
              "& .MuiDataGrid-columnSeparator": {
                color: theme.palette.primary.main,
              },
              "& .MuiButton-root": {
                color: theme.palette.primary.main,
              },
              "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
                outline: "none !important",
              },
              "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
                {
                  outline: "none !important",
                },
            }}
          />
        )}
      </Box>

      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        slotProps={{
          paper: {
            sx: {
              bgcolor: theme.palette.background.alt,
              border: `1px solid ${theme.palette.divider}`,
              "& .MuiListItemIcon-root": {
                color: theme.palette.primary.main,
              },
            },
          },
        }}
      >
        <MenuItem onClick={handleContextMenuEdit}>
          <ListItemIcon>
            <EditRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Edit
          </ListItemText>
        </MenuItem>

        <MenuItem onClick={handleContextMenuFinalizeMonth}>
          <ListItemIcon>
            <CheckCircleRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Finalize Month
          </ListItemText>
        </MenuItem>

        <MenuItem onClick={handleContextMenuDelete}>
          <ListItemIcon>
            <DeleteRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Delete
          </ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Expenses;
